import { Controller } from "@hotwired/stimulus";
import DmnModeler from "dmn-js/lib/Modeler";
import { EditorState } from "@codemirror/state";
import { EditorView } from "@codemirror/view";
import { basicSetup } from "codemirror";
import { javascript } from "@codemirror/lang-javascript";
import { json } from "@codemirror/lang-json";
import debounce from "lodash/debounce";

export default class extends Controller {
  static targets = [
    "container",
    "sourceInput",
    "input",
    "inputContainer",
    "output",
    "errorMessage",
    "bottomPane",
    "divider",
    "testButton",
  ];

  static values = {
    source: String,
    evaluationUrl: String,
    exampleInput: { type: String, default: "{}" },
  };

  connect() {
    // Log the example input value to help with debugging
    console.log("Example input value:", this.exampleInputValue);

    this.modeler = new DmnModeler({
      container: this.containerTarget,
      keyboard: {
        bindTo: document,
      },
    });

    // Load DMN diagram
    if (this.sourceValue) {
      this.modeler.importXML(this.sourceValue);
    }

    // Add event listeners for view changes
    this.modeler.on("views.changed", (event) => {
      this.handleViewChange(event.activeView);
    });

    // Initial view check after load
    this.modeler.on("import.done", () => {
      const activeView = this.modeler.getActiveView();
      if (activeView) {
        this.handleViewChange(activeView);
      }
    });

    // Initialize JSON editors for input and output
    this.initializeJsonEditor();
    this.initializeOutputEditor();

    // Add listener for form submission to update source
    document.addEventListener(
      "submit",
      this.updateSourceBeforeSubmit.bind(this)
    );
  }

  // Before form submission, update the source input
  updateSourceBeforeSubmit(event) {
    // Check if the form contains our source input
    if (event.target.contains(this.sourceInputTarget)) {
      // Prevent default to allow async operation
      event.preventDefault();

      // Save the current XML
      this.modeler.saveXML({ format: true }, (err, xml) => {
        if (!err) {
          this.sourceInputTarget.value = xml;
          // Now submit the form
          event.target.submit();
        } else {
          console.error("Error saving XML", err);
        }
      });
    }
  }

  initializeJsonEditor() {
    // Create a container for the editor
    const inputContainer = document.createElement("div");
    this.inputTarget.parentNode.replaceChild(inputContainer, this.inputTarget);

    // Initialize the JSON editor with example input or empty object
    let initialJson = "{}";

    try {
      // Log the example input value for debugging
      console.log("Processing example input:", this.exampleInputValue);

      // Check for valid input data
      if (this.exampleInputValue && this.exampleInputValue.trim() !== "") {
        // The example input should already be valid JSON string,
        // but handle potential double encoding
        try {
          // First, try to parse it directly
          const parsedExample = JSON.parse(this.exampleInputValue);
          initialJson = JSON.stringify(parsedExample, null, 2);
        } catch (parseError) {
          // If that failed, it might not be a JSON string yet
          console.log("Direct parsing failed:", parseError);
          initialJson = JSON.stringify(this.exampleInputValue, null, 2);
        }
        console.log("Formatted JSON:", initialJson);
      }
    } catch (e) {
      console.error(
        "Error parsing example input:",
        e,
        "Input was:",
        this.exampleInputValue
      );
    }

    console.log("Initial JSON for editor:", initialJson);

    this.inputState = EditorState.create({
      doc: initialJson,
      extensions: [basicSetup, json()],
    });

    this.inputEditor = new EditorView({
      state: this.inputState,
      parent: inputContainer,
    });
  }

  initializeOutputEditor() {
    // Create a container for the output editor
    const outputContainer = document.createElement("div");
    outputContainer.classList.add("w-full");
    outputContainer.style.width = "100%";
    this.outputTarget.parentNode.replaceChild(
      outputContainer,
      this.outputTarget
    );

    // Initialize the output editor as read-only
    this.outputState = EditorState.create({
      doc: "",
      extensions: [
        basicSetup,
        json(),
        EditorView.editable.of(false), // Make it read-only
        EditorState.readOnly.of(true), // Double-ensure read-only
        EditorView.theme({
          "&": {
            backgroundColor: "#f9fafb",
            width: "100%",
            maxWidth: "100%",
            height: "auto",
          },
          ".cm-content": {
            caretColor: "transparent",
            width: "100%",
          },
          ".cm-scroller": {
            overflow: "auto",
            width: "100%",
          },
          ".cm-line": {
            width: "100%",
          },
        }),
      ],
    });

    this.outputEditor = new EditorView({
      state: this.outputState,
      parent: outputContainer,
    });

    // Force width after initialization
    outputContainer
      .querySelectorAll(".cm-editor, .cm-scroller, .cm-content")
      .forEach((el) => {
        el.style.width = "100%";
      });
  }

  // Handle view changes to show/hide bottom panel
  handleViewChange(activeView) {
    if (!activeView) return;

    const isDrdView = activeView.type === "drd";

    // Show bottom pane and divider only for decision views (not DRD)
    if (isDrdView) {
      this.bottomPaneTarget.classList.add("hidden");
      // Also hide the parent divider which is the split-screen divider
      const parentDivider = this.bottomPaneTarget.previousElementSibling;
      if (
        parentDivider &&
        parentDivider.matches("[data-split-screen-target='divider']")
      ) {
        parentDivider.classList.add("hidden");
      }
      this.testButtonTarget.classList.add("opacity-50", "cursor-not-allowed");
      this.testButtonTarget.disabled = true;
    } else {
      this.bottomPaneTarget.classList.remove("hidden");
      // Also show the parent divider
      const parentDivider = this.bottomPaneTarget.previousElementSibling;
      if (
        parentDivider &&
        parentDivider.matches("[data-split-screen-target='divider']")
      ) {
        parentDivider.classList.remove("hidden");
      }
      this.testButtonTarget.classList.remove(
        "opacity-50",
        "cursor-not-allowed"
      );
      this.testButtonTarget.disabled = false;

      // Load the input data when switching to a decision view
      this.loadInputData(activeView);
    }
  }

  // Load input data based on the current view
  loadInputData(activeView) {
    if (!activeView || activeView.type === "drd") return;

    // Get the view element to find its element ID
    const view = activeView.element;
    if (!view) return;

    // Extract decision ID from the view
    const decisionId = view.id;

    // If we already have example input, use it directly for the first load
    if (
      !this.hasLoadedInitialInput &&
      this.exampleInputValue &&
      this.exampleInputValue.trim() !== ""
    ) {
      try {
        console.log(
          "Loading initial input from example:",
          this.exampleInputValue
        );

        // Try to parse the example as JSON
        let parsedExample;
        try {
          parsedExample = JSON.parse(this.exampleInputValue);
        } catch (e) {
          // If direct parsing fails, try handling it as a string that needs further processing
          console.log("Direct parsing failed, trying alternate approach");
          // Sometimes Rails might double-encode the JSON or add escape characters
          parsedExample = JSON.parse(JSON.stringify(this.exampleInputValue));
        }

        console.log("Parsed example for loading:", parsedExample);

        // Ensure we have a valid object
        if (parsedExample && typeof parsedExample === "object") {
          const formattedJson = JSON.stringify(parsedExample, null, 2);
          console.log("Formatted JSON for editor:", formattedJson);

          if (this.inputEditor) {
            this.inputEditor.dispatch({
              changes: {
                from: 0,
                to: this.inputEditor.state.doc.length,
                insert: formattedJson,
              },
            });
          }

          this.hasLoadedInitialInput = true;
          return;
        }
      } catch (e) {
        console.error(
          "Error processing example input:",
          e,
          "Value was:",
          this.exampleInputValue
        );
      }
    }

    // Otherwise, fetch from the server as before
    fetch(
      `${
        this.evaluationUrlValue
      }/default_input?decision_id=${encodeURIComponent(decisionId)}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')
            .content,
        },
      }
    )
      .then((response) => response.json())
      .then((data) => {
        if (data.input) {
          // Format the JSON for display
          const formattedJson = JSON.stringify(data.input, null, 2);
          // Update the editor content
          if (this.inputEditor) {
            this.inputEditor.dispatch({
              changes: {
                from: 0,
                to: this.inputEditor.state.doc.length,
                insert: formattedJson,
              },
            });
          }
        } else {
          // Default empty object if no input is found
          if (this.inputEditor) {
            this.inputEditor.dispatch({
              changes: {
                from: 0,
                to: this.inputEditor.state.doc.length,
                insert: "{}",
              },
            });
          }
        }
      })
      .catch((error) => {
        console.error("Error fetching default input:", error);
        if (this.inputEditor) {
          this.inputEditor.dispatch({
            changes: {
              from: 0,
              to: this.inputEditor.state.doc.length,
              insert: "{}",
            },
          });
        }
      });
  }

  // Called before form submission to update the source
  disconnect() {
    // Remove the submit listener to avoid memory leaks
    document.removeEventListener("submit", this.updateSourceBeforeSubmit);

    // Clean up modeler
    if (this.modeler) {
      this.modeler.destroy();
    }
  }

  // Test decision evaluation
  evaluateDecision(event) {
    event.preventDefault();

    // Only allow evaluation in decision view, not DRD view
    const activeView = this.modeler.getActiveView();
    if (!activeView || activeView.type === "drd") return;

    // Get the input JSON from the editor
    const inputData = this.inputEditor
      ? this.inputEditor.state.doc.toString()
      : "{}";

    try {
      // Parse input to ensure it's valid JSON
      const parsedInput = JSON.parse(inputData);

      // Save current diagram to ensure we're testing the latest version
      this.modeler.saveXML({ format: true }, (err, xml) => {
        if (err) {
          this.errorMessageTarget.textContent =
            "Error saving diagram: " + err.message;
          return;
        }

        // Clear previous output and errors
        this.updateOutputEditor("Processing...");
        this.errorMessageTarget.textContent = "";

        // Send evaluation request
        fetch(this.evaluationUrlValue, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')
              .content,
          },
          body: JSON.stringify({
            evaluation: {
              source: xml,
              input: parsedInput,
              decision_id: activeView.element.id,
            },
          }),
        })
          .then((response) => response.json())
          .then((data) => {
            this.errorMessageTarget.textContent = "";
            // Update the output editor with formatted JSON
            this.updateOutputEditor(data);
          })
          .catch((error) => {
            this.errorMessageTarget.textContent =
              "Error evaluating decision: " + error.message;
            this.updateOutputEditor("");
          });
      });
    } catch (e) {
      this.errorMessageTarget.textContent = "Invalid JSON input: " + e.message;
      this.updateOutputEditor("");
    }
  }

  // Helper method to update the output editor with new content
  updateOutputEditor(content) {
    if (!this.outputEditor) return;

    let formattedContent = "";

    try {
      // If it's a string that isn't JSON yet, just display it (like "Processing...")
      if (typeof content === "string") {
        if (content === "Processing...") {
          formattedContent = content;
        } else {
          // Try to parse it as JSON if it's a string
          const parsedContent = JSON.parse(content);
          formattedContent = JSON.stringify(parsedContent, null, 2);
        }
      } else {
        // If it's an object, stringify it with formatting
        formattedContent = JSON.stringify(content, null, 2);
      }
    } catch (e) {
      // If we can't parse it as JSON, just use the string
      formattedContent = String(content);
    }

    // Update the editor content
    this.outputEditor.dispatch({
      changes: {
        from: 0,
        to: this.outputEditor.state.doc.length,
        insert: formattedContent,
      },
    });
  }
}
